import {
  ThemeProvider,
  createTheme,
  responsiveFontSizes,
} from "@mui/material";
import { useLayoutEffect, useMemo } from "react";
import { Footer } from "./components/Footer";
import Header from "./components/Header/Header";
import { QueryClient, QueryClientProvider } from "react-query";
import { CloudCoreResponseHandlers } from "./helper/cloudcore/response/handler/CloudCoreResponseHandlers";
import { Outlet, useLocation } from "react-router-dom";
import styles from "./App.module.css";
import { AlertBox } from "./components/AlertBox";
import { useAppSelector } from "./common/state/hooks";
import { RootState } from "./common/state";
import { FloatingButton } from "./components/FloatingButton";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

declare module "@mui/material/styles" {
  interface Theme {
    status: {
      danger: string;
    };
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    status?: {
      danger?: string;
    };
  }
}

function App() {
  const { notifcation } = useAppSelector((state: RootState) => state.notificationSlice);
  const prefersDarkMode = false;
  //Scroll to Top while navigating
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  let theme = useMemo(
    () =>
      createTheme({
        typography: {
          h1: {
            fontSize: 40,
          },
          h2: {
            fontSize: 34,
          },
          h3: {
            fontSize: 24,
          },
          body1: {
            fontSize: 24,
            fontWeight: 400,
            color: "#454545",
          },
          body2: {
            fontSize: 20,
          },
          subtitle1: {
            color: "#454545",
            fontSize: "16px",
          },
          subtitle2: {
            color: "#9E9E9E",
            fontSize: "16px",
            fontWeight: 400,
          },
          fontFamily: `"Inter", "Helvetica", "Arial", sans-serif`,
        },
        palette: {
          primary: {
            main: "#44BCDB",
          },
          secondary: {
            main: "#333333",
          },
          mode: prefersDarkMode ? "dark" : "light",
          text: {
            secondary: "#9E9E9E",
          },
        }
      }),
    [prefersDarkMode]
  );

  theme = responsiveFontSizes(theme);

  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
          <div className="app">
            <Header />
            <div className={styles.container}>
              <Outlet/>
              <Footer />
              <CloudCoreResponseHandlers />
              <FloatingButton />
            </div>
          </div>
          {
            notifcation.message
            &&
            <AlertBox 
              key={notifcation.key}
              alertMessage={notifcation.message}
              severity={notifcation.severity}
              autoHideDuration={notifcation.autoHideDuration}
            />
        }
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
